/**
 * Géométrie des tuiles isométriques.
 *
 * ⚠️ Ces constantes sont **mesurées**, pas choisies. Ne pas les ajuster à l'œil — la
 * grille, la caméra et le placement se calent dessus, et un écart d'un pixel produit des
 * joints entre tuiles qu'on ne rattrape plus ensuite.
 *
 * Le losange n'est pas en 2:1. Ratio réel : 512 / 290 ≈ 1,766.
 *
 * **Provenance.** Ce rapport a d'abord été mesuré sur le pack 2D d'origine, puis
 * conservé tel quel lors de la bascule vers les sprites rendus depuis la 3D : c'est de
 * lui qu'on déduit l'angle de la caméra de rendu (`scripts/render-sprites/iso.mjs`,
 * sin θ = 290/512). Changer ces nombres obligerait à re-rendre toute la bibliothèque.
 */

export interface TileGeometry {
  /** Largeur du losange de la face supérieure, en pixels source. */
  readonly width: number;
  /** Hauteur du losange de la face supérieure, en pixels source. */
  readonly height: number;
  /**
   * Épaisseur du bloc de terre sous la face. Vaut plus d'une demi-hauteur de losange :
   * une tuile déborde donc sur la rangée suivante, ce qui rend le tri en profondeur
   * obligatoire (voir `isoEngine`).
   */
  readonly blockHeight: number;
  /** Ancrage du sprite : position du centre du losange dans le PNG, en fraction. */
  readonly anchorX: number;
  readonly anchorY: number;
}

/**
 * Tuile Synty, rendue par `npm run render:sprites`.
 *
 * **La projection ne change pas** : le losange fait toujours 512 × 290, puisque c'est
 * de ce rapport qu'on a déduit l'angle de la caméra 3D. Seule l'apparence change — et
 * c'est précisément ce qui rend la bascule sans risque pour la grille, la caméra et le
 * placement, qui continuent de raisonner sur les mêmes nombres.
 *
 * Deux traits mesurés sur le PNG rendu :
 *
 * - **L'ancre vaut 0,5 / 0,5**, par contrat : la caméra vise le centre de la face
 *   supérieure de la tuile, qui tombe donc au centre exact de l'image.
 * - **Le bloc ne fait plus que 34 px** au lieu de 150. Une tuile Synty ne déborde donc
 *   plus d'une rangée entière sur la suivante. Le tri en profondeur reste néanmoins
 *   obligatoire : les bâtiments, eux, débordent toujours.
 */
export const SYNTY_TILE: TileGeometry = {
  width: 512,
  height: 290,
  blockHeight: 34,
  anchorX: 0.5,
  anchorY: 0.5,
};

/**
 * CONTRAT DE CADRAGE DES SPRITES DE BÂTIMENT.
 *
 * Un bâtiment peut monter à sept hauteurs de losange (`doctor-t2`, mesuré). Le cadrer
 * symétriquement autour de sa base — le contrat des tuiles et des décors — doublait donc
 * son image pour n'y rien mettre : 1024 × 4574 pour `doctor-t1`, au-delà de la limite de
 * texture de 4096 px des GPU mobiles. Les bâtiments sont donc cadrés au plus juste vers
 * le bas, et leur ancre verticale n'est plus 0,5.
 *
 * **Elle n'est pas tabulée pour autant : elle se calcule.** Une table de correspondance
 * entre 192 sprites et leur ancre se désynchroniserait au premier re-rendu (règle 15).
 * Le pipeline garantit une seule chose, la même pour tous :
 *
 *     distance entre la base du modèle et le bord bas de l'image
 *         = footprint × TILE_HEIGHT × BUILDING_BELOW_FACTOR
 *
 * Deux nombres que le moteur possède déjà — l'emprise du bâtiment et la hauteur de sa
 * texture — donc aucune donnée à transporter.
 *
 * `tests/spriteFrame.test.ts` importe `scripts/render-sprites/iso.mjs` et verrouille
 * l'égalité de cette constante avec celle du pipeline : une dérive fait rougir un test,
 * elle ne fait pas flotter un bâtiment.
 */
export const BUILDING_BELOW_FACTOR = 0.75;

/**
 * Ancre verticale d'un sprite de bâtiment, en fraction de sa hauteur.
 *
 * Rend `0.5` si la hauteur est inconnue (texture pas encore chargée) : mieux vaut le
 * contrat des décors qu'un `NaN` qui enverrait le sprite hors de l'écran.
 */
export function buildingAnchorY(footprint: number, textureHeight: number): number {
  if (!Number.isFinite(textureHeight) || textureHeight <= 0) return 0.5;
  const below = footprint * SYNTY_TILE.height * BUILDING_BELOW_FACTOR;
  return 1 - below / textureHeight;
}

/** Demi-pas horizontal de la grille : décalage en x entre deux cases voisines. */
export function halfWidth(geometry: TileGeometry): number {
  return geometry.width / 2;
}

/** Demi-pas vertical de la grille : décalage en y entre deux cases voisines. */
export function halfHeight(geometry: TileGeometry): number {
  return geometry.height / 2;
}
